// --- React ---
import React, { useEffect, useCallback, useMemo } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 연결: BLE/시리얼, 기기 정보 갱신·저장 ---
import { useConnectionStore } from '@/store/connectionStore';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';

// --- 전역 UI 스토어 (토스트) ---
import { useNotificationStore } from '../../store/notificationStore';

// --- 공통 UI 컴포넌트 ---
import { GeneralConfigCard, SensorConfigCard } from '@/components/ui';
import { ConfigPageShell } from '@/components/configuration/ConfigPageShell';

// --- 공유 타입: MINI 기기 · 제약 조건 (@packages/types) ---
import {
  MiniSensorType,
  MINI_SENSOR_TYPE_INFO,
  MINI_SENSOR_TYPE_MAP,
} from '@packages/types/device/mini/sensors';
import {
  SENSOR_CONSTRAINTS,
  getMiniSensorDefaultValues,
} from '@packages/types/device/mini/mini_constraints';
import { GAS_CONC_UNIT_OPTIONS } from '@packages/types/device/common/gas_conc_unit';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

// --- 목업: MINI 스켈레톤 · 기본 설정 값 ---
import {
  MINI_SKELETON_DEVICE_INFO,
  MINI_SKELETON_CONFIG_VALUES,
  MINI_DEFAULT_CONFIG_VALUES,
} from '@/mock-data/configuratorMockData';

// --- 타입 정의 ---
import type { MiniDeviceInfo } from '@packages/types/device/mini/deviceInfo';
import type { MiniDeviceConfiguration } from '@packages/types/device/mini/deviceConfiguration';

// --- 이 페이지 전용: 검증 타입 · 상태 훅 ---
import type { FieldError } from './types/validation';
import { useConfigValidation } from './hooks/useConfigValidation';
import { useValidationState } from './hooks/useValidationState';
import { useConfigPageState } from './hooks/useConfigPageState';
import { useConfigPageController } from './hooks/useConfigPageController';

// --- 설정 파일 헬퍼 (LITE와 공유) ---
import { validateConfigFile, buildCradleMismatchMessage } from './utils/configFileHelpers';

// --- i18n 키 · 저장된 설정 타입 ---
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { type SavedConfiguration } from '../../utils/configStorage';

// --- 설정 화면 전용 레이아웃 스타일 ---
import { MiniConfigGrid } from './ConfigurationStyles';

/** 이 화면은 BELL MINI 기기 전용입니다. */
const EXPECTED_DEVICE_TYPE = DeviceType.MINI;

export const ConfigurationMini: React.FC = () => {
  const { t } = useTranslation('config');

  // ===== 스토어 접근 =====

  const { device } = useConnectionStore();
  const { showSuccess, showError } = useNotificationStore();

  // ===== 기기·설정 상태 =====

  const {
    detectedDeviceType,
    setDetectedDeviceType,
    curDevice,
    setCurDevice,
    deviceConfigData,
    setDeviceConfigData,
    editingCard,
    setEditingCard,
    resetState,
  } = useConfigPageState<MiniDeviceInfo, MiniDeviceConfiguration>(
    MINI_SKELETON_DEVICE_INFO as unknown as MiniDeviceInfo,
    { ...MINI_SKELETON_CONFIG_VALUES } as MiniDeviceConfiguration
  );

  // ===== 유효성 검증 =====

  const { validationState, hintsState, setValidationResult, hasErrors } = useValidationState(
    DeviceType.MINI
  );
  const { validateAll } = useConfigValidation(DeviceType.MINI);

  // ===== 파생 상태 (Derived) =====

  const isCompatible = useMemo(
    () => detectedDeviceType === EXPECTED_DEVICE_TYPE,
    [detectedDeviceType]
  );

  const isDeviceConnected = useMemo(() => device.type !== 'none', [device.type]);

  // ===== 연결 콜백 =====

  const onDeviceLoaded = useCallback(
    (deviceInfo: any, deviceConfig: any) => {
      if ('equipped_sensor' in deviceInfo) {
        setDetectedDeviceType(DeviceType.MINI);
        setCurDevice(deviceInfo as MiniDeviceInfo);
        setDeviceConfigData(deviceConfig as MiniDeviceConfiguration);
      } else if ('sensor_type' in deviceInfo) {
        setDetectedDeviceType(DeviceType.LITE);
      }
    },
    [setCurDevice, setDeviceConfigData, setDetectedDeviceType]
  );

  const onConnectionChange = useCallback(() => {}, []);

  const onDisconnected = useCallback(() => resetState(), [resetState]);

  const { saveDeviceConfig, refreshDeviceInfo } = useConnectionActions(
    onDeviceLoaded,
    onConnectionChange,
    onDisconnected,
    true
  );

  // ===== 유효성 검증 트리거 =====

  useEffect(() => {
    if (deviceConfigData.timestamp) {
      const result = validateAll(deviceConfigData, curDevice);
      setValidationResult(result);
    }
  }, [deviceConfigData, curDevice, validateAll, setValidationResult]);

  // ===== 버튼 활성화 조건 =====

  const canSaveOrLoad = isDeviceConnected && isCompatible && !hasErrors();

  // ===== 저장 전 변환 / 저장 후 반영 =====

  // MINI 특화: 배열 필드를 단일 값으로 변환하여 전송
  const buildConfigToSend = useCallback((data: Record<string, unknown>) => {
    const configDataToSend: Record<string, any> = { ...MINI_SKELETON_CONFIG_VALUES, ...data };
    if (Array.isArray(configDataToSend.calibration_interval)) {
      configDataToSend.calibration_interval = configDataToSend.calibration_interval[0] ?? 0;
    }
    if (Array.isArray(configDataToSend.bump_interval)) {
      configDataToSend.bump_interval = configDataToSend.bump_interval[0] ?? 0;
    }
    // 기기 시계는 "저장 순간의 현재 시각(UTC)"으로 맞춘다. (기기는 tz 없이 UTC로 보관)
    // set_date_time=1 이어야 기기가 timestamp로 RTC를 갱신한다. 항상 1로 전송.
    configDataToSend.timestamp = new Date().toISOString();
    configDataToSend.set_date_time = 1;
    return configDataToSend;
  }, []);

  const onSaved = useCallback(
    (deviceInfo: unknown, deviceConfig: unknown) => {
      setCurDevice(deviceInfo as MiniDeviceInfo);
      setDeviceConfigData(deviceConfig as MiniDeviceConfiguration);
    },
    [setCurDevice, setDeviceConfigData]
  );

  // ===== 공용 컨트롤러 (저장/로드/스토리지/모달) =====

  const {
    showConfigListModal,
    setShowConfigListModal,
    showNameInputModal,
    setShowNameInputModal,
    handleSaveToStorage,
    handleSaveWithName,
    handleLoadFromStorage,
    handleSave,
  } = useConfigPageController({
    deviceType: DeviceType.MINI,
    curDevice,
    deviceConfigData: deviceConfigData as unknown as Record<string, unknown>,
    hasErrors,
    canSaveToDevice: isDeviceConnected && isCompatible,
    saveDeviceConfig,
    refreshDeviceInfo,
    buildConfigToSend,
    onSaved,
  });

  // ===== 저장된 설정 적용 (MINI 단일 센서 호환성 검증) =====

  const handleConfigSelect = async (savedConfig: SavedConfiguration) => {
    try {
      const configData = savedConfig.configData;

      const validation = validateConfigFile(configData, DeviceType.MINI, t);
      if (!validation.isValid) {
        showError(
          i18nKeys.config.msg_fileValidationFailed,
          validation.error || i18nKeys.config.msg_unknownError
        );
        return;
      }

      if (!isDeviceConnected || !isCompatible) {
        showError(i18nKeys.config.msg_cradleConnectFailed, buildCradleMismatchMessage(t));
        return;
      }

      const refreshResult = await refreshDeviceInfo();
      if (!refreshResult.success || !refreshResult.data?.deviceInfo) {
        showError(i18nKeys.config.msg_cradleConnectFailed, buildCradleMismatchMessage(t));
        return;
      }

      const currentDeviceInfo = refreshResult.data.deviceInfo as unknown as MiniDeviceInfo;
      setCurDevice(currentDeviceInfo);

      // 센서 타입 호환성 검증 (MINI는 단일 센서이므로 equipped_sensor 비교)
      if (configData.deviceInfo && currentDeviceInfo) {
        const fileSensorType = configData.deviceInfo.equipped_sensor;
        const currentSensorType = currentDeviceInfo.equipped_sensor;

        if (fileSensorType !== currentSensorType) {
          showError(
            i18nKeys.config.error_sensorTypeMismatch,
            t(i18nKeys.config.error_sensorTypeMismatchDesc, {
              fileType: fileSensorType,
              deviceType: currentSensorType,
            })
          );
          return;
        }
      }

      if (configData.deviceConfig) {
        setDeviceConfigData(configData.deviceConfig as unknown as MiniDeviceConfiguration);
      }

      showSuccess(i18nKeys.config.msg_configLoaded, i18nKeys.config.msg_configLoaded);
    } catch {
      showError(i18nKeys.config.msg_configLoadFailed, i18nKeys.config.msg_configLoadFailed);
    }
  };

  // ===== 기본값 로드 =====

  const setDefaultValues = () => {
    const sensorType = curDevice?.equipped_sensor;
    let validSensorType = MiniSensorType.CO;

    if (typeof sensorType === 'string') {
      validSensorType = MINI_SENSOR_TYPE_MAP[sensorType] || MiniSensorType.CO;
    } else if (typeof sensorType === 'number') {
      validSensorType = sensorType as MiniSensorType;
    }

    const defaultValues = getMiniSensorDefaultValues(validSensorType);

    const configWithDefaults: any = {
      ...MINI_DEFAULT_CONFIG_VALUES,
      calibration_gas_conc: defaultValues.calibration_gas_conc,
      low_alarm: defaultValues.low_alarm,
      high_alarm: defaultValues.high_alarm,
      twa_alarm: defaultValues.twa_alarm,
      stel_alarm: defaultValues.stel_alarm,
      stel_period: defaultValues.stel_period,
      twa_period: defaultValues.twa_period,
      calibration_interval: 0,
      bump_interval: 180,
      auto_fresh_air_cal: false,
      timestamp: new Date().toISOString(),
    };

    setDeviceConfigData(configWithDefaults as unknown as MiniDeviceConfiguration);
    showSuccess(i18nKeys.config.msg_defaultValueLoaded, i18nKeys.config.msg_miniDefaultValueLoaded);
  };

  // ===== 설정 업데이트 핸들러 =====

  const handleGeneralConfigUpdate = useCallback(
    (updated: Record<string, unknown>) => {
      setDeviceConfigData(prev => ({ ...prev, ...updated }));
    },
    [setDeviceConfigData]
  );

  const handleSensorConfigUpdate = useCallback(
    (updated: Record<string, unknown>) => {
      setDeviceConfigData(prev => ({ ...prev, ...updated }));
    },
    [setDeviceConfigData]
  );

  // ===== 센서 카드 렌더링 =====

  const renderSensorCard = useMemo(() => {
    if (!curDevice || !deviceConfigData.timestamp) {
      return null;
    }

    let sensorType: number | string | undefined = curDevice?.equipped_sensor;
    if (typeof sensorType === 'string') {
      sensorType = MINI_SENSOR_TYPE_MAP[sensorType] || MiniSensorType.CO;
    }
    if (typeof sensorType !== 'number') {
      sensorType = MiniSensorType.CO;
    }

    const validSensorType = Object.values(MiniSensorType).includes(sensorType)
      ? sensorType
      : MiniSensorType.CO;

    const sensorInfo = MINI_SENSOR_TYPE_INFO[validSensorType as MiniSensorType];
    const constraints = SENSOR_CONSTRAINTS[validSensorType as MiniSensorType];

    if (!sensorInfo) {
      return <div>{t('msg_sensorInfoLoadFailed')}</div>;
    }
    if (!constraints) {
      return <div>{t('msg_sensorConstraintsLoadFailed')}</div>;
    }

    const gasConcUnit = constraints.gas_conc_unit || 0;
    const unitLabel = GAS_CONC_UNIT_OPTIONS[gasConcUnit]?.label || 'ppm';
    const deviceConfigCopy = { ...deviceConfigData };

    const extractFirstValue = (value: unknown): unknown => {
      if (Array.isArray(value)) return value[0];
      return value;
    };

    const sensorData = {
      index: 0,
      name: sensorInfo.label,
      type: { value: sensorType, label: sensorInfo.label },
      gas_conc_unit: { value: gasConcUnit, label: unitLabel },
      calibration_gas_conc: extractFirstValue(deviceConfigCopy.calibration_gas_conc),
      calibration_interval: extractFirstValue(deviceConfigCopy.calibration_interval),
      bump_interval: extractFirstValue(deviceConfigCopy.bump_interval),
      low_alarm: extractFirstValue(deviceConfigCopy.low_alarm),
      high_alarm: extractFirstValue(deviceConfigCopy.high_alarm),
      twa_alarm: extractFirstValue(deviceConfigCopy.twa_alarm),
      stel_alarm: extractFirstValue(deviceConfigCopy.stel_alarm),
      twa_period: extractFirstValue(deviceConfigCopy.twa_period),
      stel_period: extractFirstValue(deviceConfigCopy.stel_period),
      auto_fresh_air_cal: deviceConfigCopy.auto_fresh_air_cal || false,
    };

    const calibrationInterval = Array.isArray(deviceConfigCopy.calibration_interval)
      ? deviceConfigCopy.calibration_interval[0]
      : (deviceConfigCopy.calibration_interval as number | undefined as number);

    const decimalPoint = curDevice?.decimal_point_and_unit?.decimalPoint;

    return (
      <SensorConfigCard
        sensorData={sensorData}
        originalData={sensorData}
        constraint={constraints as unknown as Record<string, unknown>}
        isEdit={isDeviceConnected && isCompatible && editingCard === 0}
        isSkeleton={false}
        deviceType={DeviceType.MINI}
        isClickable={isDeviceConnected && isCompatible}
        onClick={() => {
          if (!isDeviceConnected || !isCompatible) return;
          setEditingCard(0);
        }}
        onUpdate={handleSensorConfigUpdate}
        errors={Object.fromEntries(
          Object.entries((validationState.sensors as Record<string, FieldError>) || {}).map(
            ([key, error]) => [key, error.message]
          )
        )}
        hints={(hintsState.sensors as Record<string, string>) || {}}
        calibrationDueDate={undefined}
        calibrationInterval={calibrationInterval}
        decimalPoint={decimalPoint}
      />
    );
  }, [
    curDevice,
    deviceConfigData,
    isDeviceConnected,
    isCompatible,
    editingCard,
    validationState,
    hintsState,
    handleSensorConfigUpdate,
    setEditingCard,
    t,
  ]);

  // ===== 렌더링 =====

  return (
    <ConfigPageShell
      deviceType={DeviceType.MINI}
      detectedDeviceType={detectedDeviceType}
      expectedDeviceType={EXPECTED_DEVICE_TYPE}
      isDeviceConnected={isDeviceConnected}
      isCompatible={isCompatible}
      cradleAble={false}
      onDeviceLoaded={onDeviceLoaded}
      onConnectionChange={onConnectionChange}
      onDisconnected={onDisconnected}
      onSaveToStorage={handleSaveToStorage}
      onLoadFromStorage={handleLoadFromStorage}
      onLoadDefault={setDefaultValues}
      onSave={handleSave}
      canSaveOrLoad={canSaveOrLoad}
      canLoadFromStorage={isDeviceConnected && isCompatible}
      configListModalVisible={showConfigListModal}
      onConfigSelect={handleConfigSelect}
      onConfigListClose={() => setShowConfigListModal(false)}
      nameInputModalVisible={showNameInputModal}
      onNameConfirm={handleSaveWithName}
      onNameInputClose={() => setShowNameInputModal(false)}
    >
      <MiniConfigGrid>
        <GeneralConfigCard
          configData={deviceConfigData}
          isEdit={isDeviceConnected && isCompatible && editingCard === null}
          isSkeleton={false}
          deviceType={DeviceType.MINI}
          isClickable={isDeviceConnected && isCompatible}
          onClick={() => {
            if (!isDeviceConnected || !isCompatible) return;
            setEditingCard(null);
          }}
          constraints={{
            datalog_interval: { unit: 'sec' },
            intelliflash_interval: { unit: 'sec' },
          }}
          onUpdate={handleGeneralConfigUpdate}
          errors={Object.fromEntries(
            Object.entries(validationState.general).map(([key, error]) => [key, error.message])
          )}
          hints={hintsState.general || {}}
          sensorCount={2}
        />
        {renderSensorCard}
      </MiniConfigGrid>
    </ConfigPageShell>
  );
};
